import * as Sentry from '@sentry/react';
import clevertap from 'clevertap-web-sdk';
import React from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { createRoutesFromChildren, matchRoutes } from 'react-router';

// Import the actual types from the app model
import type { UserDetails as AppUserDetails, MerchantTeam } from 'containers/app/app.model';

const SENTRY_CONFIG = {
  dsn: process.env.REACT_APP_SENTRY_DSN,
  tracesSampleRate: 1.0,
  environment: process.env.REACT_APP_ENV,
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 1.0,
  sendDefaultPii: true,

  // Logs
  enableLogs: true,

  // Integrations
  integrations: [
    // React Router v6 integration for better navigation tracking
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
      beforeErrorSampling: event => {
        // Check if the error is a TypeError
        const isTypeError = event.exception?.values?.some(exception => exception.type === 'TypeError');

        // Check if the error level is 'info'
        const isInfoLevel = event.level === 'info';

        // Return false to prevent replay for TypeError or info level errors
        return !(isTypeError || isInfoLevel);
      }
    })
  ],

  // Error filtering
  beforeSend(event) {
    // Filter out common noise
    if (event.exception) {
      const error = event.exception.values?.[0];
      const errorMessage = error?.value || '';

      // Common non-critical errors to filter
      const noisePatterns = [
        'ResizeObserver loop limit exceeded',
        'ChunkLoadError',
        'Loading chunk',
        'Non-Error promise rejection captured',
        'Script error',
        // eslint-disable-next-line max-len
        'n["".concat(e,"InternalOnly")] is not a function. (In \'n["".concat(e,"InternalOnly")](t)\', \'n["".concat(e,"InternalOnly")]\' is undefined)',
        'n["".concat(...)] is not a function'
      ];

      if (noisePatterns.some(pattern => errorMessage.includes(pattern))) {
        return null;
      }
    }

    const avoidUrlPatterns = [
      /\/sign-in$/,
      /\/sign-up$/,
      /https:\/\/web\.scanpay\.tech\/sign-in$/,
      /https:\/\/web\.scanpay\.tech\/sign-up$/
    ];

    const eventUrl = event.request?.url;
    if (eventUrl && avoidUrlPatterns.some(pattern => pattern.test(eventUrl))) {
      return null;
    }

    return event;
  }
};

export const initializeSentry = (): void => {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.init(SENTRY_CONFIG);
  }
};

const getClevertapUserId = (): string | null => {
  try {
    // Get CleverTap user ID
    const clevertapId = clevertap.getCleverTapID();
    return clevertapId || null;
  } catch (error) {
    // Silently fail if CleverTap is not initialized or available
    return null;
  }
};

// Function to update Sentry user context
export const updateSentryUserContext = (
  userDetails?: AppUserDetails | null,
  currentTeam?: MerchantTeam | null
): void => {
  try {
    // Set minimal user context - only ID and name
    if (userDetails && userDetails.id) {
      Sentry.setUser({
        id: userDetails.id,
        username: userDetails.name
      });
      Sentry.setTag('phone', userDetails.phone);
    } else {
      // Try to set CleverTap ID as fallback if no user details
      const clevertapUserId = getClevertapUserId();
      if (clevertapUserId) {
        Sentry.setUser({ clevertap_user_id: clevertapUserId });
      }
    }

    if (currentTeam && currentTeam.id) {
      Sentry.setTags({
        team_id: currentTeam.id.toString()
      });
    }

    // Always set CleverTap ID as tag for correlation
    const clevertapUserId = getClevertapUserId();
    if (clevertapUserId) {
      Sentry.setTag('clevertap_user_id', clevertapUserId);
    }
  } catch (error) {
    //error
  }
};

export default Sentry;
